import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { FormControl } from "@mui/material";
import { DashboardFinanceiroChart } from "../../../../domain/models/dashboard_financeiro_chart";
import { useCallback, useEffect, useState } from "react";
import { DefaultDatePicker } from "../../../../../../common/components/masked_inputs/default_date_picker_input";
import { ModalLoading } from "../../../../../../common/components/modal_loading";


type FluxoCaixaChartProps = {
    loading: boolean;
    dados?: DashboardFinanceiroChart[];
    ano?: number;
    mes?: number;
    onAnoChange: (ano: number) => void;
    onMesChange: (mes?: number) => void;
};


const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(valor);
};

const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        const recebido = payload.find((entry: any) => entry.dataKey === "recebido")?.value || 0;
        const pago = payload.find((entry: any) => entry.dataKey === "pago")?.value || 0;
        const saldo = recebido - pago;

        return (
            <div className="rounded-lg border border-zinc-200 bg-white p-3 shadow-lg dark:border-zinc-700 dark:bg-zinc-800">
                <p className="font-semibold text-black dark:text-white">
                    {payload[0].payload.mes}
                </p>
                {payload.map((entry: any, index: number) => (
                    <p key={index} style={{ color: entry.color }} className="text-sm">
                        {entry.name}: {formatarMoeda(entry.value)}
                    </p>
                ))}
                <div className="mt-2 border-t border-zinc-200 pt-2 dark:border-zinc-700">
                    <p className="text-sm font-semibold text-black dark:text-white">
                        Saldo: {formatarMoeda(saldo)}
                    </p>
                </div>
            </div>
        );
    }
    return null;
};

export const FluxoCaixaChart = ({
    dados,
    ano,
    mes,
    loading,
    onAnoChange,
    onMesChange,
}: FluxoCaixaChartProps) => {
    const [selectedAno, setSelectedAno] = useState<Date>(ano ? new Date(ano, 0, 1) : new Date());
    const [selectedMes, setSelectedMes] = useState<Date | undefined>(mes ? new Date(0, mes - 1, 1) : undefined);

    const handleAnoChange = useCallback((novoAno: number) => {
        setSelectedAno(new Date(novoAno, 0, 1));
        setSelectedMes(undefined);
        onAnoChange(novoAno);
    }, [onAnoChange]);

    const handleMesChange = useCallback((novoMes: number | undefined) => {
        if (novoMes !== undefined) {
            setSelectedMes(new Date(0, novoMes - 1, 1));
        } else {
            setSelectedMes(undefined);
        }
        onMesChange(novoMes);
    }, [onMesChange]);

    useEffect(() => {
        setSelectedAno(ano ? new Date(ano, 0, 1) : new Date());
    }, [ano]);

    useEffect(() => {
        setSelectedMes(mes ? new Date(0, mes - 1, 1) : undefined);
    }, [mes]);

    return (
        <div className="w-full rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
            {/* Header com título e seletor de ano */}
            <ModalLoading loading={loading}  >


                <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <div>
                        <h2 className="text-lg font-medium text-black dark:text-white">
                            Fluxo de Caixa {selectedAno.getFullYear()}
                        </h2>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400">
                            Evolução do fluxo de caixa por mês
                        </p>
                    </div>

                    <FormControl className="w-52 flex-row gap-4 justify-end">

                        <DefaultDatePicker
                            name={""}
                            label="Ano"
                            views={['year']}
                            tipoData="ano"
                            value={selectedAno}
                            onChangeValue={(date) => {
                                if (date) {
                                    const novoAno = new Date(date).getFullYear();
                                    handleAnoChange(novoAno);
                                } else {
                                    handleAnoChange(new Date().getFullYear());
                                }
                            }}
                        />

                        <DefaultDatePicker
                            name={""}
                            label="Mês"
                            views={['month']}
                            tipoData="meses"
                            value={selectedMes ?? null}
                            onChangeValue={(date) => {
                                if (date) {
                                    const novoMes = new Date(date).getMonth() + 1;
                                    handleMesChange(novoMes);
                                } else {
                                    handleMesChange(undefined);
                                }
                            }}
                        />

                    </FormControl>
                </div>

                {/* Gráfico */}
                <div className="w-full overflow-x-auto">
                    <ResponsiveContainer width="100%" height={400} minWidth={600}>
                        <LineChart
                            data={dados}
                            margin={{ top: 20, right: 30, left: 80, bottom: 20 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e4e4e7"
                                className="dark:stroke-zinc-700"
                            />

                            <XAxis
                                dataKey="mes"
                                stroke="#71717a"
                                style={{ fontSize: "12px" }}
                            />

                            <YAxis
                                stroke="#71717a"
                                style={{ fontSize: "12px" }}
                                tickFormatter={(value) => formatarMoeda(value)}
                            />

                            <Tooltip content={<CustomTooltip />} />

                            <Legend
                                wrapperStyle={{ paddingTop: "20px" }}
                                iconType="line"
                            />

                            {/* Linha A Pagar - Verde */}
                            <Line
                                type="monotone"
                                dataKey="aPagar"
                                stroke="#22c55e"
                                name="A Pagar"
                                strokeWidth={2}
                                dot={{ fill: "#22c55e", r: 4 }}
                                activeDot={{ r: 6 }}
                            />

                            {/* Linha A Receber - Rosa/Vermelho */}
                            <Line
                                type="monotone"
                                dataKey="aReceber"
                                stroke="#ec4899"
                                name="A Receber"
                                strokeWidth={2}
                                dot={{ fill: "#ec4899", r: 4 }}
                                activeDot={{ r: 6 }}
                            />

                            {/* Linha Pago - Amarelo */}
                            <Line
                                type="monotone"
                                dataKey="pago"
                                stroke="#eab308"
                                name="Pago"
                                strokeWidth={2}
                                dot={{ fill: "#eab308", r: 4 }}
                                activeDot={{ r: 6 }}
                            />

                            {/* Linha Recebido - Azul */}
                            <Line
                                type="monotone"
                                dataKey="recebido"
                                stroke="#3b82f6"
                                name="Recebido"
                                strokeWidth={2}
                                dot={{ fill: "#3b82f6", r: 4 }}
                                activeDot={{ r: 6 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Legenda detalhada */}
                {/* <div className="mt-6  grid grid-cols-2 gap-3 sm:grid-cols-5">
                    {[
                        { label: "A Pagar", color: "bg-green-500" },
                        { label: "A Receber", color: "bg-pink-500" },
                        { label: "Pago", color: "bg-yellow-500" },
                        { label: "Recebido", color: "bg-blue-500" },
                    ].map((item) => (
                        <div key={item.label} className="flex items-center gap-2">
                            <span className={`h-3 w-3 rounded-full ${item.color}`} />
                            <p className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                                {item.label}
                            </p>
                        </div>
                    ))}
                </div> */}

            </ModalLoading>
        </div>
    );
};